import { useState } from 'react';
import MuiTextField from '@material-ui/core/TextField';
import MuiButton from '@material-ui/core/Button';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';
import type {
  LogoUrlUiOptions,
  LogoUrlValue,
} from '../../schemas/logoUrlSchema';

type LogoInputMode = 'url' | 'upload';

const PREVIEW_SIZE = 88;
/** Matches the outlined small/dense TextField's rendered height, so the
 * control area is the same size in both modes and the preview box next to
 * it never shifts vertically when switching between URL and Upload. */
const CONTROL_AREA_HEIGHT = 40;

/** Logos only ever render as small icons, so downscaling to this covers
 * even a 2x-retina display at the largest size we show them at. */
const MAX_LOGO_DIMENSION = 512;
const COMPRESSED_IMAGE_QUALITY = 0.85;

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to decode image'));
    image.src = dataUrl;
  });

/**
 * Photos exported from design tools can carry megabytes of pixel data and
 * embedded metadata (ICC profiles, XMP/C2PA blocks). Stored inline as a
 * data: URL, that becomes a base64 string long enough to break GitHub's
 * file editor. Downscaling through a canvas keeps the icon sharp at its
 * (small) display size, discards the embedded metadata, and shrinks the
 * encoded output to a few KB.
 */
const compressImage = async (dataUrl: string): Promise<string> => {
  const image = await loadImage(dataUrl);
  const scale = Math.min(
    1,
    MAX_LOGO_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight),
  );
  const width = Math.round(image.naturalWidth * scale) || 1;
  const height = Math.round(image.naturalHeight * scale) || 1;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    return dataUrl;
  }
  context.drawImage(image, 0, 0, width, height);

  const webp = canvas.toDataURL('image/webp', COMPRESSED_IMAGE_QUALITY);
  return webp.startsWith('data:image/webp') ? webp : canvas.toDataURL('image/png');
};

export const LogoUrlField = (
  props: FieldExtensionComponentProps<LogoUrlValue, LogoUrlUiOptions>,
) => {
  const { formData, onChange, schema, required, rawErrors, idSchema } = props;
  const [mode, setMode] = useState<LogoInputMode>(
    formData?.startsWith('data:') ? 'upload' : 'url',
  );

  const handleFile = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    const rawDataUrl = await readFileAsDataUrl(file);
    // SVGs are already compact text and lose their scalability if rasterized.
    if (file.type === 'image/svg+xml') {
      onChange(rawDataUrl);
      return;
    }
    try {
      onChange(await compressImage(rawDataUrl));
    } catch {
      onChange(rawDataUrl);
    }
  };

  return (
    <ScaffolderField
      rawDescription={schema.description}
      rawErrors={rawErrors}
      required={required}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            role="radiogroup"
            aria-label="Logo input mode"
            style={{
              display: 'inline-flex',
              padding: 2,
              borderRadius: 999,
              background: 'rgba(74, 104, 221, 0.08)',
            }}
          >
            {(['url', 'upload'] as const).map(option => {
              const active = mode === option;
              return (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setMode(option)}
                  style={{
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 14px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 650,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    background: active ? 'var(--rdap-blue-500, #4a68dd)' : 'transparent',
                    boxShadow: active
                      ? '0 1px 4px rgba(74, 104, 221, 0.45)'
                      : 'none',
                    color: active ? '#fff' : 'rgba(74, 104, 221, 0.6)',
                    transition:
                      'background 0.15s ease, box-shadow 0.15s ease, color 0.15s ease',
                  }}
                >
                  {option === 'url' ? 'URL' : 'Upload'}
                </button>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 8,
              minHeight: CONTROL_AREA_HEIGHT,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {mode === 'url' ? (
              <MuiTextField
                id={idSchema?.$id}
                size="small"
                margin="dense"
                variant="outlined"
                style={{ width: '50%' }}
                placeholder="https://example.com/logo.svg"
                value={formData ?? ''}
                required={required}
                error={Boolean(rawErrors && rawErrors.length > 0)}
                onChange={event => onChange(event.target.value)}
              />
            ) : (
              <MuiButton
                variant="outlined"
                component="label"
                size="small"
                startIcon={<CloudUploadOutlinedIcon fontSize="small" />}
                style={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 600,
                  paddingLeft: 14,
                  paddingRight: 14,
                }}
              >
                Choose image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={event => handleFile(event.target.files?.[0])}
                />
              </MuiButton>
            )}
          </div>
        </div>

        <div
          style={{
            width: PREVIEW_SIZE,
            height: PREVIEW_SIZE,
            flexShrink: 0,
            borderRadius: 4,
            border: '1px solid rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            background: 'rgba(0, 0, 0, 0.03)',
          }}
        >
          {formData ? (
            <img
              src={formData}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <ImageOutlinedIcon style={{ opacity: 0.35 }} />
          )}
        </div>
      </div>
    </ScaffolderField>
  );
};
